/** Web DI seam: device-id is read async from IndexedDB, so a first-paint submit omits X-Reflecto-Device-Id (best-effort; header optional). */

import type { FeedbackModalLabels, FeedbackPayload } from "@reflecto/client-ui";
import { FeedbackModal as HeadlessFeedbackModal } from "@reflecto/client-ui";
import { useCallback, useEffect, useState } from "react";
import { webStorage } from "../../adapters/storage.web";
import { messages } from "../../i18n";
import { submitFeedback } from "../../services/feedback";

const LABELS: FeedbackModalLabels = {
  title: messages.feedback.title,
  close: messages.feedback.close,
  messageLabel: messages.feedback.messageLabel,
  messagePlaceholder: messages.feedback.messagePlaceholder,
  categoryLabel: messages.feedback.categoryLabel,
  categoryBug: messages.feedback.categoryBug,
  categoryIdea: messages.feedback.categoryIdea,
  categoryOther: messages.feedback.categoryOther,
  emailLabel: messages.feedback.emailLabel,
  emailPlaceholder: messages.feedback.emailPlaceholder,
  emailHelp: messages.feedback.emailHelp,
  disclosure: messages.feedback.disclosure,
  submit: messages.feedback.submit,
  sending: messages.feedback.sending,
  successTitle: messages.feedback.success,
  errorMessage: messages.feedback.error,
  done: messages.feedback.done,
};

export function FeedbackModal({
  onClose,
  onSendingChange,
}: {
  onClose: () => void;
  onSendingChange?: (sending: boolean) => void;
}) {
  const [deviceId, setDeviceId] = useState<string | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    webStorage
      .get("deviceId")
      .then(({ deviceId: id }) => {
        if (!cancelled) setDeviceId(id);
      })
      .catch(() => {
        // Best-effort: the device id is optional correlation metadata.
        // If the read fails we just omit the header.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubmit = useCallback(
    async (payload: FeedbackPayload) => {
      try {
        // Inside try so it always pairs with finally's (false), even on a pre-flight throw.
        onSendingChange?.(true);
        await submitFeedback(payload, { surface: "webapp", deviceId });
      } finally {
        onSendingChange?.(false);
      }
    },
    [deviceId, onSendingChange]
  );

  return <HeadlessFeedbackModal labels={LABELS} onSubmit={handleSubmit} onClose={onClose} />;
}
